
import { Plus, Minus, Trash2 } from 'lucide-react';
import { getUnitPriceForQuantity, type CatalogProduct } from '../api/delivery';
import { useCartStore } from '../store/cart';
import { fmt, type Branding } from '../lib/branding';

// Extraído de StorePage.tsx (12/07/2026) pra poder ser reaproveitado no rail
// de upsell da página de produto (ProductPage.tsx), sem duplicar o card do
// zero. Comportamento visual idêntico ao anterior; único acréscimo é o
// `onOpen`, disparado ao clicar na imagem/nome (não nos botões de
// adicionar/quantidade, que continuam existindo e chamando o carrinho
// direto -- clicar em "Adicionar" no rail de upsell não deve abrir a
// página do produto).
export function ProductCard({
  product,
  brand,
  onOpen,
}: {
  product: CatalogProduct;
  brand: Branding;
  onOpen?: () => void;
}) {
  const { addItem, items, updateQuantity, removeItem } = useCartStore();
  const cartItem = items.find((i) => i.product.id === product.id);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
      <div
        className={`w-full h-36 bg-gray-50 flex items-center justify-center overflow-hidden ${onOpen ? 'cursor-pointer' : ''}`}
        onClick={onOpen}
        role={onOpen ? 'button' : undefined}
        aria-label={onOpen ? `Ver detalhes de ${product.name}` : undefined}
      >
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="w-full h-full object-contain p-2" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p
          className={`text-sm font-semibold text-gray-800 leading-tight line-clamp-2 ${onOpen ? 'cursor-pointer' : ''}`}
          onClick={onOpen}
        >
          {product.name}
        </p>
        {product.deliveryDescription && (
          <p className="text-xs text-gray-500 line-clamp-2 leading-snug">{product.deliveryDescription}</p>
        )}
        {!product.deliveryDescription && product.description?.toLowerCase().startsWith('economize') && (
          <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-md w-fit">
            🏷️ {product.description.split('·')[0].trim()}
          </span>
        )}
        {!product.deliveryDescription && product.description && !product.description.toLowerCase().startsWith('economize') && (
          <p className="text-xs text-gray-400 line-clamp-2 leading-snug">{product.description}</p>
        )}
        {!product.deliveryDescription && product.description?.toLowerCase().startsWith('economize') && (
          <p className="text-xs text-gray-400 line-clamp-1 leading-snug">{product.description.split('·').slice(1).join('·').trim()}</p>
        )}
        {/* Delivery price display */}
        <div className="mt-auto">
          {product.hasCustomDeliveryPrice && product.deliveryPriceCents < product.salePriceCents ? (
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-xs text-gray-400 line-through">{fmt(product.salePriceCents)}</span>
              <span style={{ color: brand.accentColor }} className="text-sm font-bold">{fmt(product.deliveryPriceCents)}</span>
              <span className="text-xs bg-green-100 text-green-700 font-semibold px-1 py-0.5 rounded-md">-{Math.round((1 - product.deliveryPriceCents / product.salePriceCents) * 100)}%</span>
            </div>
          ) : (
            <p style={{ color: brand.accentColor }} className="text-sm font-bold">{fmt(product.deliveryPriceCents)}</p>
          )}
        </div>
        {/* Selo de desconto progressivo (leve mais, pague menos) -- só
            aparece pra produtos elegíveis (bebidas avulsas, ver
            calcularPrecoProgressivo no backend).
            12/07/2026 (revisão de design): era uma caixa com fundo e borda,
            mesmo peso visual do preço principal -- numa grade com vários
            produtos elegíveis ao mesmo tempo, isso poluía a tela e disputava
            atenção com o preço. Virou uma linha de texto simples, sem caixa,
            mesma informação com menos peso. */}
        {product.bulkPricing && product.bulkPricing.length > 0 && (
          <p className="text-[10px] font-medium text-amber-600 leading-tight">
            🔥 {product.bulkPricing.map((t) => `${t.qty}${t.qty === 4 ? '+' : ''}un ${fmt(t.unitPriceCents)}/un`).join(' · ')}
          </p>
        )}
        {!cartItem ? (
          <button
            onClick={() => addItem(product)}
            style={{ backgroundColor: brand.primaryColor }}
            className="w-full text-white text-sm font-semibold py-2 rounded-xl transition-opacity active:opacity-80"
          >
            Adicionar
          </button>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-2 py-1">
              <button
                onClick={() => cartItem.quantity === 100 ? removeItem(product.id) : updateQuantity(product.id, cartItem.quantity - 100)}
                style={{ color: brand.primaryColor }}
                className="p-1 rounded-lg active:opacity-70"
              >
                {cartItem.quantity === 100 ? <Trash2 size={16} /> : <Minus size={16} />}
              </button>
              <span className="text-sm font-bold text-gray-800">{cartItem.quantity / 100}</span>
              <button
                onClick={() => updateQuantity(product.id, cartItem.quantity + 100)}
                style={{ color: brand.primaryColor }}
                className="p-1 rounded-lg active:opacity-70"
              >
                <Plus size={16} />
              </button>
            </div>
            {product.bulkPricing && product.bulkPricing.length > 0 && cartItem.quantity > 100 && (
              <p className="text-[11px] text-green-700 font-semibold text-center">
                {fmt(getUnitPriceForQuantity(product, cartItem.quantity / 100))}/un com desconto aplicado
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
